// components/Outreach/StatsTab.js
import React, { useState, useEffect, useMemo } from "react";
import { getCookie } from "../../services/cookie";

const API_BASE = "https://flautreach.com/flautreach/api/outreach";

export function StatsTab({ add }) {
  const token = () => getCookie("authToken");
  const getHeaders = () => {
    const t = token();
    return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  };

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Фильтры
  const [period, setPeriod] = useState("30d"); // 7d | 30d | 90d | all
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [workerFilter, setWorkerFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("day"); // day | week | month
  const [metricType, setMetricType] = useState("all"); // all | outreach | replies | rate

  useEffect(() => { loadStats(); }, [period]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const days = period === "7d" ? 7 : period === "90d" ? 90 : period === "all" ? 365 : 30;
      const resp = await fetch(`${API_BASE}/stats/?days=${days}`, { headers: getHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setStats(data);
      }
    } catch (err) {
      add?.("Ошибка загрузки статистики", "error");
    } finally {
      setLoading(false);
    }
  };

  // Фильтрованные данные
  const filteredData = useMemo(() => {
    if (!stats) return null;

    // Фильтруем daily_stats по кампаниям/воркерам/платформам
    let dailyStats = [...(stats.daily_stats || [])];
    
    // Фильтруем per_worker
    let perWorker = (stats.per_worker || []).filter(w => {
      if (workerFilter !== "all" && w.name !== workerFilter) return false;
      return true;
    });

    // Фильтруем per_campaign
    let perCampaign = (stats.per_campaign || []).filter(c => {
      if (campaignFilter !== "all" && c.name !== campaignFilter) return false;
      return true;
    });

    // Аггрегируем метрики по выбранным фильтрам
    let aggregatedMetrics = {
      outreach_total: 0,
      outreach_today: 0,
      incoming_total: 0,
      incoming_today: 0,
      our_replies_total: 0,
      our_replies_today: 0,
      failed_total: 0,
      failed_today: 0,
      reply_rate: 0,
      pending_count: stats.pending_count || 0,
      targets_total: 0,
      targets_today: 0,
    };

    if (workerFilter === "all" && campaignFilter === "all") {
      // Без фильтров — глобальные цифры
      aggregatedMetrics = {
        outreach_total: stats.outreach_total,
        outreach_today: stats.outreach_today,
        incoming_total: stats.incoming_total,
        incoming_today: stats.incoming_today,
        our_replies_total: stats.our_replies_total,
        our_replies_today: stats.our_replies_today,
        failed_total: stats.failed_total,
        failed_today: stats.failed_today,
        reply_rate: stats.reply_rate,
        pending_count: stats.pending_count,
        targets_total: stats.targets_total,
        targets_today: stats.targets_today,
      };
    } else if (workerFilter !== "all") {
      const worker = perWorker[0];
      if (worker) {
        aggregatedMetrics.outreach_total = worker.outreach_total;
        aggregatedMetrics.outreach_today = worker.outreach_today;
        aggregatedMetrics.incoming_total = worker.incoming_total;
        aggregatedMetrics.incoming_today = worker.incoming_today;
        aggregatedMetrics.failed_total = worker.failed_total;
        aggregatedMetrics.failed_today = worker.failed_today;
        aggregatedMetrics.reply_rate = worker.outreach_total > 0 
          ? Math.round((worker.incoming_total / worker.outreach_total) * 1000) / 10 
          : 0;
      }
    } else if (campaignFilter !== "all") {
      const campaign = perCampaign[0];
      if (campaign) {
        aggregatedMetrics.outreach_total = campaign.outreach_total;
        aggregatedMetrics.incoming_total = campaign.incoming_total;
        aggregatedMetrics.our_replies_total = campaign.our_replies;
        aggregatedMetrics.failed_total = campaign.failed_total;
        aggregatedMetrics.pending_count = campaign.pending;
        aggregatedMetrics.reply_rate = campaign.reply_rate;
      }
    }

    // Группируем daily stats по неделям/месяцам
    let groupedStats = dailyStats;
    if (groupBy === "week") {
      groupedStats = groupByWeek(dailyStats);
    } else if (groupBy === "month") {
      groupedStats = groupByMonth(dailyStats);
    }

    return {
      dailyStats: groupedStats,
      perWorker,
      perCampaign,
      hourlyStats: stats.hourly_stats || [],
      platformStats: stats.platform_stats || {},
      aggregatedMetrics,
    };
  }, [stats, workerFilter, campaignFilter, platformFilter, groupBy]);

  // Группировка по неделям
  const groupByWeek = (dailyData) => {
    const weeks = {};
    dailyData.forEach(day => {
      const date = new Date(day.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().slice(0, 10);
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = { date: weekKey, outreach: 0, incoming: 0, replies: 0, failed: 0 };
      }
      weeks[weekKey].outreach += day.outreach;
      weeks[weekKey].incoming += day.incoming;
      weeks[weekKey].replies += day.replies;
      weeks[weekKey].failed += day.failed;
    });
    return Object.values(weeks).sort((a, b) => a.date.localeCompare(b.date));
  };

  // Группировка по месяцам
  const groupByMonth = (dailyData) => {
    const months = {};
    dailyData.forEach(day => {
      const monthKey = day.date.slice(0, 7);
      if (!months[monthKey]) {
        months[monthKey] = { date: monthKey + "-01", outreach: 0, incoming: 0, replies: 0, failed: 0 };
      }
      months[monthKey].outreach += day.outreach;
      months[monthKey].incoming += day.incoming;
      months[monthKey].replies += day.replies;
      months[monthKey].failed += day.failed;
    });
    return Object.values(months).sort((a, b) => a.date.localeCompare(b.date));
  };

  // Тренды (рост/падение за последние 7 дней vs предыдущие 7)
  const trends = useMemo(() => {
    if (!stats?.daily_stats || stats.daily_stats.length < 14) return null;
    
    const all = stats.daily_stats;
    const recent = all.slice(-7);
    const previous = all.slice(-14, -7);
    
    const sum = (arr, key) => arr.reduce((acc, d) => acc + (d[key] || 0), 0);
    
    const recentOutreach = sum(recent, 'outreach');
    const prevOutreach = sum(previous, 'outreach');
    const recentIncoming = sum(recent, 'incoming');
    const prevIncoming = sum(previous, 'incoming');
    
    const calcTrend = (recent, prev) => {
      if (prev === 0) return recent > 0 ? 100 : 0;
      return Math.round(((recent - prev) / prev) * 100);
    };
    
    return {
      outreach: calcTrend(recentOutreach, prevOutreach),
      incoming: calcTrend(recentIncoming, prevIncoming),
    };
  }, [stats?.daily_stats]);

  if (loading) return <div style={{ textAlign: "center", padding: 40 }}><div className="spinner"/></div>;
  if (!stats) return <div className="empty-state"><h3>Нет данных</h3></div>;

  const tabs = [
    { key: "overview", label: "📊 Обзор" },
    { key: "timeline", label: "📈 Графики" },
    { key: "workers", label: "👷 Воркеры" },
    { key: "campaigns", label: "📢 Кампании" },
    { key: "breakdown", label: "🔍 Детализация" },
  ];

  const m = filteredData?.aggregatedMetrics || {};

  return (
    <div>
      {/* Табы */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, flexWrap: "wrap" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "1px solid var(--border)",
              background: activeTab === t.key ? "var(--blue)" : "#fff",
              color: activeTab === t.key ? "#fff" : "var(--dark)",
              cursor: "pointer", fontSize: 13, fontWeight: 600,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Фильтры */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <select value={period} onChange={e => setPeriod(e.target.value)}
          style={filterSelectStyle}>
          <option value="7d">7 дней</option>
          <option value="30d">30 дней</option>
          <option value="90d">90 дней</option>
          <option value="all">Всё время</option>
        </select>
        
        <select value={campaignFilter} onChange={e => setCampaignFilter(e.target.value)}
          style={filterSelectStyle}>
          <option value="all">Все кампании</option>
          {(stats.per_campaign || []).map(c => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </select>
        
        <select value={workerFilter} onChange={e => setWorkerFilter(e.target.value)}
          style={filterSelectStyle}>
          <option value="all">Все воркеры</option>
          {(stats.per_worker || []).map(w => (
            <option key={w.id} value={w.name}>{w.name}</option>
          ))}
        </select>

        <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)}
          style={filterSelectStyle}>
          <option value="all">Все платформы</option>
          <option value="EMAIL">Email</option>
          <option value="FB_MESSENGER">FB Messenger</option>
          <option value="CONTACT_FORM">Contact Form</option>
        </select>

        <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
          style={filterSelectStyle}>
          <option value="day">По дням</option>
          <option value="week">По неделям</option>
          <option value="month">По месяцам</option>
        </select>
      </div>

      {/* ========== ОБЗОР ========== */}
      {activeTab === "overview" && (
        <>
          {/* KPI Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
            <KPICard icon="📤" label="Отправлено сегодня" value={m.outreach_today} total={m.outreach_total} color="#3b82f6" trend={trends?.outreach} />
            <KPICard icon="📥" label="Получено сегодня" value={m.incoming_today} total={m.incoming_total} color="#16a34a" trend={trends?.incoming} />
            <KPICard icon="💬" label="Наши ответы" value={m.our_replies_today} total={m.our_replies_total} color="#f59e0b" />
            <KPICard icon="❌" label="Ошибок" value={m.failed_today} total={m.failed_total} color="#dc2626" />
            <KPICard icon="📈" label="Reply Rate" value={`${m.reply_rate}%`} color="#8b5cf6" />
            <KPICard icon="⏳" label="В очереди" value={m.pending_count} color="#f97316" />
          </div>

          {/* Конверсионная воронка */}
          <div className="section-card" style={{ marginBottom: 16 }}>
            <h3>🔄 Воронка коммуникации</h3>
            <FunnelChart stats={m} />
          </div>

          {/* Платформы */}
          {filteredData?.platformStats && Object.keys(filteredData.platformStats).length > 0 && (
            <div className="section-card">
              <h3>📡 По платформам</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginTop: 12 }}>
                {Object.entries(filteredData.platformStats).map(([platform, count]) => (
                  <PlatformCard key={platform} platform={platform} count={count} total={m.outreach_total} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== ГРАФИКИ ========== */}
      {activeTab === "timeline" && (
        <>
          {/* График */}
          <div className="section-card" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3>📈 Динамика ({groupBy === "day" ? "по дням" : groupBy === "week" ? "по неделям" : "по месяцам"})</h3>
              <div style={{ display: "flex", gap: 8 }}>
                {["all", "outreach", "replies", "rate"].map(type => (
                  <button key={type} onClick={() => setMetricType(type)}
                    style={{
                      padding: "4px 10px", borderRadius: 12, border: "1px solid var(--border)",
                      background: metricType === type ? "var(--blue)" : "#fff",
                      color: metricType === type ? "#fff" : "var(--dark)",
                      cursor: "pointer", fontSize: 11,
                    }}>
                    {type === "all" ? "Всё" : type === "outreach" ? "Отправка" : type === "replies" ? "Ответы" : "Rate"}
                  </button>
                ))}
              </div>
            </div>
            
            <LineChart 
              data={filteredData?.dailyStats || []} 
              metricType={metricType}
              maxValue={Math.max(...(filteredData?.dailyStats || []).map(d => 
                metricType === "rate" 
                  ? (d.outreach > 0 ? (d.incoming / d.outreach) * 100 : 0)
                  : Math.max(d.outreach, d.incoming, d.replies)
              ), 1)}
            />

            {/* Легенда */}
            <div style={{ display: "flex", gap: 16, marginTop: 16, fontSize: 12 }}>
              <LegendItem color="#3b82f6" label="Отправлено" />
              <LegendItem color="#16a34a" label="Получено" />
              <LegendItem color="#f59e0b" label="Ответы" />
              <LegendItem color="#dc2626" label="Ошибки" />
            </div>
          </div>

          {/* Активность по часам */}
          <div className="section-card">
            <h3>⏰ Активность по часам (сегодня)</h3>
            <Heatmap hours={stats.hourly_stats || []} />
          </div>
        </>
      )}

      {/* ========== ВОРКЕРЫ ========== */}
      {activeTab === "workers" && (
        <div className="section-card">
          <h3>👷 Эффективность воркеров</h3>
          <div style={{ overflow: "auto", marginTop: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={thStyle}>Воркер</th>
                  <th style={thStyle}>Статус</th>
                  <th style={thStyle}>Лимит</th>
                  <th style={thStyle}>Отправлено</th>
                  <th style={thStyle}>Входящие</th>
                  <th style={thStyle}>Reply Rate</th>
                  <th style={thStyle}>Ошибки</th>
                  <th style={thStyle}>Эффективность</th>
                  <th style={thStyle}>Осталось</th>
                </tr>
              </thead>
              <tbody>
                {(filteredData?.perWorker || []).map(w => {
                  const efficiency = w.daily_limit > 0 ? Math.round((w.outreach_today / w.daily_limit) * 100) : 0;
                  const replyRate = w.outreach_today > 0 ? Math.round((w.incoming_today / w.outreach_today) * 100) : 0;
                  
                  return (
                    <tr key={w.id} style={{ borderBottom: "1px solid #f3f4f6", background: w.is_active ? "#fff" : "#fafafa" }}>
                      <td style={tdStyle}><strong>{w.name}</strong></td>
                      <td style={tdStyle}><StatusBadge active={w.is_active} /></td>
                      <td style={tdStyle}>{w.daily_limit}</td>
                      <td style={{ ...tdStyle }}>
                        <div style={{ fontWeight: 700, color: "#3b82f6" }}>{w.outreach_today}</div>
                        <div style={{ fontSize: 10, color: "#9ca3af" }}>всего: {w.outreach_total}</div>
                      </td>
                      <td style={{ ...tdStyle, color: w.incoming_today > 0 ? "#16a34a" : "#9ca3af" }}>{w.incoming_today}</td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ flex: 1, height: 6, background: "#e5e7eb", borderRadius: 3 }}>
                            <div style={{ width: `${replyRate}%`, height: "100%", background: replyRate > 10 ? "#16a34a" : "#f59e0b", borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600 }}>{replyRate}%</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: w.failed_today > 0 ? "#dc2626" : "#9ca3af" }}>{w.failed_today}</td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ flex: 1, height: 6, background: "#e5e7eb", borderRadius: 3 }}>
                            <div style={{ 
                              width: `${efficiency}%`, 
                              height: "100%", 
                              background: efficiency > 80 ? "#16a34a" : efficiency > 40 ? "#f59e0b" : "#dc2626",
                              borderRadius: 3 
                            }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600 }}>{efficiency}%</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: w.remaining <= 0 ? "#dc2626" : "#16a34a" }}>
                        {w.remaining}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========== КАМПАНИИ ========== */}
      {activeTab === "campaigns" && (
        <div className="section-card">
          <h3>📢 Результативность кампаний</h3>
          <div style={{ overflow: "auto", marginTop: 12 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={thStyle}>Кампания</th>
                  <th style={thStyle}>Прогресс</th>
                  <th style={thStyle}>Отправлено</th>
                  <th style={thStyle}>Входящие</th>
                  <th style={thStyle}>Reply Rate</th>
                  <th style={thStyle}>Ошибки</th>
                  <th style={thStyle}>Pending</th>
                  <th style={thStyle}>Конверсия</th>
                  <th style={thStyle}>Платформы</th>
                </tr>
              </thead>
              <tbody>
                {(filteredData?.perCampaign || []).map(c => {
                  const progress = c.intentions_total > 0 
                    ? Math.round(((c.outreach_total + c.failed_total) / c.intentions_total) * 100) 
                    : 0;
                  const conversionRate = c.outreach_total > 0 
                    ? Math.round((c.incoming_total / c.outreach_total) * 100) 
                    : 0;
                  
                  return (
                    <tr key={c.id} style={{ borderBottom: "1px solid #f3f4f6", background: c.is_active ? "#fff" : "#fafafa" }}>
                      <td style={tdStyle}>
                        <strong>{c.name}</strong>
                        <StatusBadge active={c.is_active} />
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ flex: 1, height: 6, background: "#e5e7eb", borderRadius: 3 }}>
                            <div style={{ width: `${progress}%`, height: "100%", background: "#3b82f6", borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600 }}>{progress}%</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: "#3b82f6" }}>{c.outreach_total}</td>
                      <td style={{ ...tdStyle, color: c.incoming_total > 0 ? "#16a34a" : "#9ca3af" }}>{c.incoming_total}</td>
                      <td style={tdStyle}>
                        <span style={{ 
                          color: c.reply_rate > 20 ? "#16a34a" : c.reply_rate > 5 ? "#f59e0b" : "#9ca3af",
                          fontWeight: 600 
                        }}>
                          {c.reply_rate}%
                        </span>
                      </td>
                      <td style={{ ...tdStyle, color: c.failed_total > 0 ? "#dc2626" : "#9ca3af" }}>{c.failed_total}</td>
                      <td style={{ ...tdStyle, color: c.pending > 0 ? "#f97316" : "#9ca3af", fontWeight: 600 }}>
                        {c.pending}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ color: conversionRate > 10 ? "#16a34a" : "#9ca3af", fontWeight: 600 }}>
                          {conversionRate}%
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", gap: 2 }}>
                          {(c.platforms || []).slice(0, 3).map(p => (
                            <span key={p} style={{
                              padding: "1px 6px", borderRadius: 8, fontSize: 10,
                              background: "#e0e7ff", color: "#3730a3",
                            }}>
                              {p.slice(0, 3)}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========== ДЕТАЛИЗАЦИЯ ========== */}
      {activeTab === "breakdown" && (
        <div style={{ display: "grid", gap: 16 }}>
          {/* Сводка по фильтрам */}
          <div className="section-card">
            <h3>🔍 Детальный разбор</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginTop: 12 }}>
              <MetricBox label="Всего отправлено" value={m.outreach_total} sub={`Сегодня: ${m.outreach_today}`} />
              <MetricBox label="Всего ответов" value={m.incoming_total} sub={`Сегодня: ${m.incoming_today}`} />
              <MetricBox label="Наши ответы" value={m.our_replies_total} sub={`Сегодня: ${m.our_replies_today}`} />
              <MetricBox label="Ошибки" value={m.failed_total} sub={`Сегодня: ${m.failed_today}`} />
              <MetricBox label="Reply Rate" value={`${m.reply_rate}%`} sub="Отправленные → Входящие" />
              <MetricBox label="В очереди" value={m.pending_count} sub="Pending intentions" />
            </div>
          </div>

          {/* Топ-5 воркеров */}
          <div className="section-card">
            <h3>🏆 Топ воркеров</h3>
            <TopPerformers data={(stats.per_worker || []).sort((a, b) => b.outreach_today - a.outreach_today).slice(0, 5)} />
          </div>

          {/* Топ-5 кампаний */}
          <div className="section-card">
            <h3>🎯 Топ кампаний по ответам</h3>
            <TopPerformers data={(stats.per_campaign || []).sort((a, b) => b.reply_rate - a.reply_rate).slice(0, 5)} type="campaign" />
          </div>
        </div>
      )}
    </div>
  );
}

// ========== КОМПОНЕНТЫ ==========

function KPICard({ icon, label, value, total, color, trend }) {
  return (
    <div style={{
      padding: "16px", background: "#fff", borderRadius: 12,
      border: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <span style={{ fontSize: 28 }}>{icon}</span>
        {trend !== undefined && (
          <span style={{
            fontSize: 12, fontWeight: 600,
            color: trend > 0 ? "#16a34a" : trend < 0 ? "#dc2626" : "#9ca3af",
          }}>
            {trend > 0 ? "↑" : "↓"} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color, marginTop: 8 }}>{value}</div>
      <div style={{ fontSize: 12, color: "#6b7280" }}>{label}</div>
      {total !== undefined && (
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>Всего: {total}</div>
      )}
    </div>
  );
}

function FunnelChart({ stats }) {
  const stages = [
    { label: "Intentions", value: (stats.outreach_total || 0) + (stats.pending_count || 0), color: "#e0e7ff" },
    { label: "Отправлено", value: stats.outreach_total || 0, color: "#3b82f6" },
    { label: "Получено ответов", value: stats.incoming_total || 0, color: "#16a34a" },
    { label: "Наши ответы", value: stats.our_replies_total || 0, color: "#f59e0b" },
  ];

  const maxVal = Math.max(...stages.map(s => s.value), 1);

  return (
    <div style={{ marginTop: 12 }}>
      {stages.map((stage, i) => {
        const width = Math.max((stage.value / maxVal) * 100, 2);
        return (
          <div key={i} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
              <span style={{ fontWeight: 600 }}>{stage.label}</span>
              <span style={{ color: "#6b7280" }}>{stage.value}</span>
            </div>
            <div style={{ height: 28, background: "#f3f4f6", borderRadius: 6, overflow: "hidden" }}>
              <div style={{
                width: `${width}%`, height: "100%", background: stage.color,
                borderRadius: 6, display: "flex", alignItems: "center", paddingLeft: 8,
                fontSize: 11, color: i > 0 ? "#fff" : "#4b5563", fontWeight: 600,
                transition: "width 0.5s ease",
              }}>
                {width > 15 && `${stage.value}`}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LineChart({ data, metricType, maxValue }) {
  if (!data.length) return <div style={{ color: "#9ca3af", padding: 20, textAlign: "center" }}>Нет данных для графика</div>;

  const chartHeight = 200;
  const pointSpacing = 100 / (data.length - 1 || 1);

  const getY = (val) => 100 - (val / maxValue) * 100;

  const buildPath = (key) => {
    if (metricType !== "all" && metricType !== "outreach" && key !== "incoming" && key !== "replies") return "";
    if (metricType === "replies" && key === "outreach") return "";
    if (metricType === "rate" && key !== "rate") return "";
    
    return data.map((d, i) => {
      let val = d[key];
      if (metricType === "rate" && key === "rate") {
        val = d.outreach > 0 ? (d.incoming / d.outreach) * 100 : 0;
      }
      return `${i === 0 ? "M" : "L"} ${i * pointSpacing} ${getY(val)}`;
    }).join(" ");
  };

  const buildArea = (key) => {
    const path = buildPath(key);
    if (!path) return "";
    return `${path} L ${(data.length - 1) * pointSpacing} 100 L 0 100 Z`;
  };

  const shouldShow = (key) => {
    if (metricType === "all") return true;
    if (metricType === "outreach" && key === "outreach") return true;
    if (metricType === "replies" && (key === "incoming" || key === "replies")) return true;
    if (metricType === "rate" && key === "rate") return true;
    return false;
  };

  return (
    <div style={{ position: "relative", width: "100%", paddingTop: `${chartHeight}px` }}>
      <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} viewBox={`0 0 100 100`} preserveAspectRatio="none">
        {/* Горизонтальные линии сетки */}
        {[0, 25, 50, 75, 100].map(y => (
          <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="#f3f4f6" strokeWidth="0.5" />
        ))}

        {/* Области под графиками */}
        {shouldShow("outreach") && <path d={buildArea("outreach")} fill="url(#outreachGrad)" opacity="0.2" />}
        {shouldShow("incoming") && <path d={buildArea("incoming")} fill="url(#incomingGrad)" opacity="0.2" />}
        {shouldShow("replies") && <path d={buildArea("replies")} fill="url(#repliesGrad)" opacity="0.2" />}

        {/* Линии */}
        {shouldShow("outreach") && <path d={buildPath("outreach")} stroke="#3b82f6" strokeWidth="1.5" fill="none" />}
        {shouldShow("incoming") && <path d={buildPath("incoming")} stroke="#16a34a" strokeWidth="1.5" fill="none" />}
        {shouldShow("replies") && <path d={buildPath("replies")} stroke="#f59e0b" strokeWidth="1.5" fill="none" />}
        {shouldShow("failed") && <path d={buildPath("failed")} stroke="#dc2626" strokeWidth="1" fill="none" strokeDasharray="3,3" />}

        {/* Точки на графике */}
        {data.map((d, i) => {
          if (shouldShow("outreach") && d.outreach > 0) {
            return <circle key={`o-${i}`} cx={i * pointSpacing} cy={getY(d.outreach)} r="1.5" fill="#3b82f6" />;
          }
          return null;
        })}

        {/* Градиенты */}
        <defs>
          <linearGradient id="outreachGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="incomingGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#16a34a" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#16a34a" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="repliesGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

function Heatmap({ hours }) {
  const maxVal = Math.max(...hours.map(h => h.count), 1);
  
  return (
    <div style={{ display: "flex", gap: 2, marginTop: 12, alignItems: "flex-end", height: 100 }}>
      {hours.map((h, i) => {
        const height = (h.count / maxVal) * 100;
        const intensity = h.count / maxVal;
        
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{
              width: "100%", 
              height: `${Math.max(height, 2)}%`,
              background: `rgba(59, 130, 246, ${0.2 + intensity * 0.8})`,
              borderRadius: "2px 2px 0 0",
              transition: "height 0.3s",
              minHeight: h.count > 0 ? 4 : 0,
            }} />
            <div style={{ fontSize: 9, color: "#9ca3af", marginTop: 4 }}>
              {h.hour}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PlatformCard({ platform, count, total }) {
  const icons = { EMAIL: "📧", FB_MESSENGER: "💬", CONTACT_FORM: "📝" };
  const share = total > 0 ? Math.round((count / total) * 100) : 0;
  
  return (
    <div style={{
      padding: "14px", background: "#f8faff", borderRadius: 10,
      border: "1px solid #e0e7ff", display: "flex", alignItems: "center", gap: 12,
    }}>
      <span style={{ fontSize: 24 }}>{icons[platform] || "📡"}</span>
      <div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>{platform}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#1e40af" }}>{count}</div>
        <div style={{ fontSize: 10, color: "#9ca3af" }}>{share}% от всех</div>
      </div>
    </div>
  );
}

function TopPerformers({ data, type = "worker" }) {
  return (
    <div style={{ marginTop: 12 }}>
      {data.map((item, i) => (
        <div key={i} style={{
          display: "flex", alignItems: "center", gap: 12, padding: "10px 0",
          borderBottom: i < data.length - 1 ? "1px solid #f3f4f6" : "none",
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", background: i < 3 ? "#fef3c7" : "#f3f4f6",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700, color: i < 3 ? "#92400e" : "#9ca3af",
          }}>
            {i + 1}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {type === "worker" ? item.name : item.name}
            </div>
            {type === "worker" && (
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                Отправлено: {item.outreach_today} | Reply Rate: {item.outreach_today > 0 ? Math.round((item.incoming_today / item.outreach_today) * 100) : 0}%
              </div>
            )}
            {type === "campaign" && (
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                Reply Rate: {item.reply_rate}% | Отправлено: {item.outreach_total}
              </div>
            )}
          </div>
          <div style={{
            fontSize: 18, fontWeight: 800,
            color: type === "worker" ? "#3b82f6" : "#16a34a",
          }}>
            {type === "worker" ? item.outreach_today : `${item.reply_rate}%`}
          </div>
        </div>
      ))}
    </div>
  );
}

function MetricBox({ label, value, sub }) {
  return (
    <div style={{ padding: "14px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
      <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#1f2937" }}>{value}</div>
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function StatusBadge({ active }) {
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 10, fontSize: 11, marginLeft: 6,
      background: active ? "#dcfce7" : "#fee2e2",
      color: active ? "#16a34a" : "#dc2626",
    }}>
      {active ? "Активен" : "Неактивен"}
    </span>
  );
}

function LegendItem({ color, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 12, height: 12, background: color, borderRadius: 3 }} />
      <span>{label}</span>
    </div>
  );
}

// Стили
const filterSelectStyle = {
  padding: "6px 12px", borderRadius: 6, border: "1px solid var(--border)",
  background: "#fff", fontSize: 12, cursor: "pointer", outline: "none",
};

const thStyle = { padding: "10px 12px", textAlign: "left", fontWeight: 600, color: "#6b7280", fontSize: 12, whiteSpace: "nowrap" };
const tdStyle = { padding: "10px 12px", fontSize: 13, whiteSpace: "nowrap" };